<template>
  <Popup
    v-if="headerPopupVisible"
    :anchor="headerPopupAnchor"
    :show="headerPopupVisible"
    popup-class="header-popup-content"
  >
    {{ headerPopupText }}
  </Popup>
  <Grid
    class="assignments-grid"
    :pageable="pageable"
    :data-items="assignments"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :selected-field="selectedField"
    :sort="sort"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @selectionchange="changeAssignmentSelection"
    @sortchange="sortChangeHandler"
    @rowclick="changeAssignmentSelection"
    data-cy="assignments-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e: any) => props.onClosemenu(e)"
        @contentfocus="(e: any) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:SelectHeaderCell>
      <th>
        <input
          type="checkbox"
          :checked="allPageAssignmentsSelected"
          :disabled="showSelectionPopup"
          ref="selectAllCheckbox"
          @change="onHeaderSelectionChange"
          data-cy="select-all-checkbox"
        />
        <Popup
          class="actions-popup"
          anchor="selectAllCheckbox"
          :show="showSelectionPopup"
          popup-class="popup-content"
          :anchor-align="{ horizontal: 'right', vertical: 'middle' }"
          :popup-align="{ horizontal: 'left', vertical: 'middle' }"
        >
          <div style="height: 60px" class="content popup-inner-content">
            <p>
              {{ selectionHeaderChecked ? "Select" : "Deselect" }} all {{ total }} items or just the
              {{ assignments.length }} items on this page?
            </p>
            <Button
              v-for="action in popupActions"
              :key="action.text"
              :theme-color="action.theme"
              @click="action.onClick"
              data-cy="select-all-popup-button"
            >
              {{ action.text }}
            </Button>
          </div>
        </Popup>
      </th>
    </template>
    <template v-slot:LastRfrPlantVisitHeaderCell>
      <div
        ref="lastRfrPlantVisitDiv"
        @mouseover="
          showHeaderPopup('lastRfrPlantVisitDiv', 'Last Plant Visit is the last visit to the plant by any RFR')
        "
        @mouseleave="hideHeaderPopup"
      >
        Last Plant Visit
        <font-awesome-icon icon="question-circle" />
      </div>
    </template>
    <template v-slot:LastPlantVisitHeaderCell>
      <div
        ref="lastPlantVisitDiv"
        @mouseover="showHeaderPopup('lastPlantVisitDiv', 'My Last Plant Visit is your last visit to the plant')"
        @mouseleave="hideHeaderPopup"
      >
        My Last Plant Visit
        <font-awesome-icon icon="question-circle" />
      </div>
    </template>
    <template v-slot:AssignedRatioHeaderCell>
      <div
        ref="assignedRatioDiv"
        @mouseover="showHeaderPopup('assignedRatioDiv', 'On-Site vs. Virtual visit ratio assigned to RFR')"
        @mouseleave="hideHeaderPopup"
      >
        Assigned Ratio
        <font-awesome-icon icon="question-circle" />
      </div>
    </template>
    <template v-slot:YtdVisitCountsHeaderCell>
      <div
        ref="ytdVisitCountsDiv"
        @mouseover="
          showHeaderPopup(
            'ytdVisitCountsDiv',
            'Total Year-To-Date On-Site and Virtual plant visits. Displays RFR\'s regular visits.'
          )
        "
        @mouseleave="hideHeaderPopup"
      >
        YTD Visit Counts
        <font-awesome-icon icon="question-circle" />
      </div>
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <button
          v-if="dataItem.isGroupPlant && dataItem.VisitTypeEnum == EnumVisitType.Regular"
          type="button"
          class="group-details-icon"
          title="Group details"
          aria-label="Group details"
          @click.prevent="openDetails(dataItem)"
          data-cy="group-details-button"
        >
          <font-awesome-icon icon="users" aria-hidden="true" />
        </button>
      </td>
    </template>
    <template v-slot:VisitDetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <a
          v-if="dataItem.NextVisitId"
          class="visit-link plant-visit-details-icon"
          :href="Pages.PlantVisitDetails.replace(':visitId', String(dataItem.NextVisitId))"
          target="_blank"
          rel="noopener noreferrer"
          title="Open visit details"
          aria-label="Open visit details"
          @click.stop
          data-cy="plant-visit-details-link"
        >
          <font-awesome-icon icon="circle-info" aria-hidden="true" />
        </a>
      </td>
    </template>
    <template v-slot:LastRfrPlantVisitCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <a
          v-if="lastRfrVisitLinkEnabled(dataItem)"
          class="visit-link"
          :href="`${Pages.VisitInspectionForm}?type=Inspection&mode=view&visitID=${getLastRfrVisitId(dataItem)}`"
        >
          {{ getLastRfrPlantVisit(dataItem) }}</a
        >
        <span v-else class="visit-span">
          {{ getLastRfrPlantVisit(dataItem) }}
        </span>
      </td>
    </template>
    <template v-slot:LastPlantVisitCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <span v-if="getMyLastPlantVisit(dataItem) === 'NEW'" class="new-visit">NEW!!</span>
        <a
          v-else-if="myLastVisitLinkEnabled(dataItem)"
          class="visit-link"
          :class="{ late: dataItem.IsLate }"
          :href="`${Pages.VisitInspectionForm}?type=Inspection&mode=view&visitID=${getMyLastVisitId(dataItem)}`"
        >
          {{ getMyLastPlantVisit(dataItem) }}</a
        >
        <span v-else class="visit-span" :class="{ late: dataItem.IsLate }">
          {{ getMyLastPlantVisit(dataItem) }}
        </span>
      </td>
    </template>
    <template v-slot:PlannedVisitDateCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <span data-cy="planned-visit-date">
          {{ getPlannedVisitDate(dataItem) }}
        </span>
      </td>
    </template>
    <template v-slot:PlantNameCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] }}
        <a
          :href="`/oudirect/PlantHistory/${dataItem.PlantID}`"
          target="_blank"
          class="plant-history-link"
          data-cy="plant-history-link"
        >
          <font-awesome-icon icon="history" aria-hidden="true" />
        </a>
      </td>
    </template>
    <template v-slot:RCCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <span v-if="dataItem.MainCompany" class="rc-email-action" @click="sendEmailClicked(dataItem)"
          >{{ dataItem[field] }}
        </span>
        <span v-else>
          {{ dataItem[field] }}
        </span>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
  type GridRowClickEvent,
  type GridSelectionChangeEvent,
} from "@progress/kendo-vue-grid";
import { ref, computed, watchEffect, watch } from "vue";
import type { CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import { Popup } from "@progress/kendo-vue-popup";
import { Button } from "@progress/kendo-vue-buttons";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { AssignmentWithSelection } from "@/api/rfrAssignments";
import {
  getLastRfrPlantVisit,
  getLastRfrVisitId,
  getMyLastPlantVisit,
  getMyLastVisitId,
  lastRfrVisitLinkEnabled,
  myLastVisitLinkEnabled,
} from "@/shared/helpers/assignmentsHelper";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import { Pages } from "@/pages/pages";
import { getFilterOperatorsForDate } from "@/shared/helpers/filtersHelper";

const props = defineProps<{
  assignments: AssignmentWithSelection[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (column: GridColumnPropsExtended) => Promise<AssignmentWithSelection[]>;
  teamAssignments: boolean;
}>();

const emit = defineEmits<{
  (e: "assignmentsChanged", skip: number, take: number, filterEvent: GridFilterChangeEvent | null): void;
  (e: "selectionChanged", selectedProducts: AssignmentWithSelection[]): void;
  (e: "selectAllClicked", checked: boolean): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "sendEmailClicked", assignment: AssignmentWithSelection): void;
  (e: "detailsClicked", assignment: AssignmentWithSelection): void;
}>();

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);
const showSelectionPopup = ref<boolean>(false);
const selectionHeaderChecked = ref<boolean>(false);

const headerPopupVisible = ref(false);
const headerPopupAnchor = ref("");
const headerPopupText = ref("");

const selectedField = "selected";

const getColumns = function () {
  return [
    {
      field: "selected",
      width: "50px",
      sortable: false,
      filterable: false,
      headerCell: "SelectHeaderCell",
      headerClassName: "hide-menu",
    },   
    {
      title: "Assignment Details",
      field: "NextVisitId",
      cell: "VisitDetailsCell",
      headerClassName: "hide-menu",
      sortable: false,
      filterable: false,
      width: "60px",
    },
    {
      field: "LastVisitDate",
      cell: "LastRfrPlantVisitCell",
      headerCell: "LastRfrPlantVisitHeaderCell",
      columnMenu: "columnMenu",
      allowedFilterOperators: getFilterOperatorsForDate(),
    },
    {
      title: "My Last Plant Visit",
      field: "LastVisitDateRFR",
      cell: "LastPlantVisitCell",
      headerCell: "LastPlantVisitHeaderCell",
      columnMenu: "columnMenu",
      allowedFilterOperators: getFilterOperatorsForDate(),
    },
    {
      title: "Planned Visit Date",
      field: "PlannedVisitDate",
      cell: "PlannedVisitDateCell",
      columnMenu: "columnMenu",
      allowedFilterOperators: getFilterOperatorsForDate(),
    },
    {
      title: "Visit Frequency",
      field: "VisitFrequencyWithASAP",
      columnMenu: "columnMenu",
    },
    {
      title: "Season Period",
      field: "SeasonalPeriod",
      columnMenu: "columnMenu",
      hidden: props.assignments.every((a) => !a.IsSeasonal),
    },
    {
      title: "Visit Type",
      field: "VisitType",
      columnMenu: "columnMenu",
    },
    {
      title: "Company Name",
      field: "CompanyName",
      columnMenu: "columnMenu",
    },
    {
      title: "Plant Name",
      field: "PlantName",
      cell: "PlantNameCell",
      columnMenu: "columnMenu",
    },
    {
      title: "Plant Status",
      field: "OwnsStatus",
      columnMenu: "columnMenu",
    },
    {
      title: "Group",
      cell: "DetailsCell",
      headerClassName: "hide-menu",
      sortable: false,
      filterable: false,
      width: "60px",
    },
    {
      title: "City",
      field: "PlantCity",
      columnMenu: "columnMenu",
    },
    {
      title: "State",
      field: "PlantState",
      columnMenu: "columnMenu",
    },
    {
      title: "Country",
      field: "PlantCountry",
      columnMenu: "columnMenu",
    },
    {
      title: "RFR",
      field: "RfrLastFirstName",
      columnMenu: "columnMenu",
      hidden: !props.teamAssignments,
    },
    {
      title: "RC",
      field: "RC",
      cell: "RCCell",
      columnMenu: "columnMenu",
    },
    {
      title: "Assigned Ratio",
      field: "AssignedRatio",
      headerCell: "AssignedRatioHeaderCell",
      columnMenu: "columnMenu",
    },
    {
      title: "YTD Visit History",
      field: "YtdVisitCounts",
      headerCell: "YtdVisitCountsHeaderCell",
      columnMenu: "columnMenu",
    },
  ];
};

const columns = ref<GridColumnPropsExtended[]>(getColumns());

const popupActions = [
  {
    text: "All",
    theme: "success",
    onClick: () => {
      props.assignments.forEach((a) => (a.selected = selectionHeaderChecked.value));
      emit("selectAllClicked", selectionHeaderChecked.value);
      showSelectionPopup.value = false;
    },
  },
  {
    text: "Page",
    theme: "warning",
    onClick: () => {
      props.assignments.forEach((a) => (a.selected = selectionHeaderChecked.value));
      emit("selectionChanged", props.assignments);
      showSelectionPopup.value = false;
    },
  },
];

let pageSizes = [10, 20, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);

const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const allPageAssignmentsSelected = computed(() => {
  return props.assignments.every((a) => a.selected);
});

const showHeaderPopup = function (refValue: string, text: string) {
  headerPopupAnchor.value = refValue;
  headerPopupText.value = text;
  headerPopupVisible.value = true;
};

const hideHeaderPopup = function () {
  headerPopupVisible.value = false;
};

const sendEmailClicked = function (assignment: AssignmentWithSelection) {
  emit("sendEmailClicked", assignment);
};

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("assignmentsChanged", 0, take.value, e);
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("assignmentsChanged", event.page.skip, event.page.take, null);
};

const onHeaderSelectionChange = function (event: Event) {
  showSelectionPopup.value = true;
  selectionHeaderChecked.value = (event.target as HTMLInputElement).checked;
};

const changeAssignmentSelection = function (event: GridSelectionChangeEvent | GridRowClickEvent) {
  // If clicked on the RC link, RFR visit link, My visit link or the details icon
  // don't change the selection
  const className = event.event?.target?.className;
  if (
    className.includes &&
    (className.includes("rc-email-action") ||
      className.includes("visit-link") ||
      className.includes("details-icon") ||
      className.includes("planned-visit-date-action"))
  )
    return;

  const assignment = event.dataItem as AssignmentWithSelection;
  if (!assignment) return;
  assignment.selected = !assignment.selected;
  emit("selectionChanged", [assignment]);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) columns.value = columnsProps;
  console.debug("AssignmentsGrid.vue, onColumnSubmit", columnsProps);
};

const openDetails = function (assignment: AssignmentWithSelection) {
  emit("detailsClicked", assignment);
};

const getPlannedVisitDate = function (assignment: AssignmentWithSelection) {
  return assignment.PlannedVisitDate ? new Date(assignment.PlannedVisitDate).toLocaleDateString() : "";
};

watchEffect(() => {
  columns.value.find((c) => c.field === "SeasonalPeriod")!.hidden = props.assignments.every((a) => !a.IsSeasonal);
});

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});

watch(
  () => props.teamAssignments,
  () => {
    columns.value = getColumns();
  }
);
</script>

<style scoped lang="scss">
.assignments-grid {
  min-height: 450px;

  .rc-email-action {
    color: #337ab7;
    cursor: pointer;
  }

  .fa-circle-question {
    color: #7aa8d0;
    margin-left: 4px;
    cursor: pointer;
  }

  .group-details-icon {
    border: 0;
    padding: 4px;
    color: #337ab7;
    background: transparent;
    cursor: pointer;

    &:hover {
      color: #23527c;
    }
  }

  .new-visit {
    display: inline-block;
    background: #40b0a6;
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    text-transform: uppercase;
  }

  .visit-link,
  .plant-history-link {
    color: #337ab7;
    cursor: pointer;
    padding: 4px;

    &:hover {
      color: #23527c;
    }

    &.late {
      color: red;
      font-weight: bold;
    }
  }

  .plant-visit-details-icon {
    margin-left: 4px;
  }

  .visit-span {
    &.late {
      color: red;
      font-weight: bold;
    }
  }
}

.header-popup-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.k-grid {
  a.is-download-link {
    svg {
      height: 12px;
    }

    &:before {
      content: "";
    }
  }
}

.actions-popup {
  .content {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
  }

  &:after {
    content: "";
    position: absolute;
    border: 10px solid transparent;
    bottom: 17px;
    left: -20px;
    border-right-color: #f9f9f9;
  }
}
</style>
<style>
.k-pager-sizes .k-dropdownlist {
  min-width: 5.5rem;
}
</style>
